import { PricingConfig } from '@editor/types/Pricing';
import { SupportedCurrency } from '@editor/types/SubscriptionPrice';
import {
  SubscriptionPlanInterval,
  SubscriptionPlanType,
  SubscriptionTierType,
} from '@repo/subscription/api';

import {
  FREE_PLAN_SINGLE_USE_CREDITS_AMOUNT,
  GetPricingConfigParams,
  getAddons,
  getComparison,
  getExpertCreditsLabel,
  getFindPrice,
  getUnlimitedInfoIconModalContentData,
} from './pricingConfig';

// @TODO: This config will be renamed to 'pricingConfig' as part of the cleanup ticket https://app.clickup.com/t/86bzvq45k.

export const LATEST_PRO_DESIGN_LIMIT = 50;
export const LATEST_FREE_DESIGN_LIMIT = 5;

export const getOnClickBusinessLink = ({
  email,
  name,
}: {
  email?: string | null;
  name?: string | null;
}): string => {
  const typeformUrl =
    process.env.REACT_APP_BUSINESS_PLAN_TYPEFORM_URL ||
    process.env.NEXT_PUBLIC_BUSINESS_PLAN_TYPEFORM_URL;
  if (!typeformUrl) {
    return '';
  }
  return `${typeformUrl}#email=${email ?? ''}&name=${name ?? ''}`;
};

const getDesignLimits = (
  designLimits?: Record<SubscriptionPlanType, number>
): { free: number; pro: number | string } => {
  let proDesignsLimit: string | number =
    designLimits?.PRO ?? LATEST_PRO_DESIGN_LIMIT;
  if (proDesignsLimit === 0) {
    proDesignsLimit = 'Unlimited';
  }
  return {
    free: designLimits?.FREE ?? LATEST_FREE_DESIGN_LIMIT,
    pro: proDesignsLimit,
  };
};

export const getNewPricingConfig = ({
  growthBook,
  creditLimits,
  designLimits,
  selectedExpertAddon,
  subscriptionPrices,
  country,
}: GetPricingConfigParams): PricingConfig => {
  const isWorkspacesEnabled = growthBook?.isOn('workspaces');
  const findPrice = getFindPrice({
    subscriptionPrices,
    country,
  });
  const proMonthlyPrice = findPrice(
    SubscriptionTierType.PRO,
    SubscriptionPlanInterval.monthly
  );
  const proYearlyPrice = findPrice(
    SubscriptionTierType.PRO,
    SubscriptionPlanInterval.yearly
  );
  const expertMonthlyPrice = findPrice(
    SubscriptionTierType.EXPERT,
    SubscriptionPlanInterval.monthly
  );
  const expertYearlyPrice = findPrice(
    SubscriptionTierType.EXPERT,
    SubscriptionPlanInterval.yearly
  );
  // Business plan prices are mainly for display purposes, they can't be purchased through the app, so it is ok they are not fetched from the backend for  now.
  const businessMonthlyPrice = {
    id: 'business-monthly',
    amount: 9000,
    currency: SupportedCurrency.USD,
  };
  const businessYearlyPrice = {
    id: 'business-yearly',
    amount: 6500,
    currency: SupportedCurrency.USD,
  };

  return {
    [SubscriptionTierType.FREE]: {
      title: 'Free',
      description:
        'Start creating stunning designs with professional templates.',
      buttonLabel: 'Get Free',
      price: {
        monthly: {
          id: '',
          amount: 0,
          currency: proMonthlyPrice.currency,
        },
        yearly: {
          id: '',
          amount: 0,
          currency: proMonthlyPrice.currency,
        },
        label: 'Free forever',
      },
      featuresLabel: 'Features:',
      mainFeatures: [
        { label: `${getDesignLimits(designLimits).free} Projects`, bold: true },
        {
          label: `${FREE_PLAN_SINGLE_USE_CREDITS_AMOUNT} Credits / month per editor`,
          bold: true,
        },
        { label: '0.5GB file storage', bold: true },
      ],
      otherFeatures: [
        { label: '10,000+ professional templates' },
        { label: '1M+ photos, graphics and fonts' },
        { label: 'High-fidelity Mockups' },
      ],
      comparison: getComparison('FREE', growthBook, { creditLimits }),
    },
    [SubscriptionTierType.PRO]: {
      title: 'Pro',
      description:
        'Great for individuals and small teams who need access to vector files, advanced editing and AI tools.',
      buttonLabel: 'Get Pro',
      price: {
        monthly: {
          id: proMonthlyPrice.id,
          amount: proMonthlyPrice.amount,
          currency: proMonthlyPrice.currency,
        },
        yearly: {
          id: proYearlyPrice.id,
          amount: proYearlyPrice.amount / 12,
          currency: proYearlyPrice.currency,
        },
      },
      featuresLabel: 'Everything in Free and...',
      mainFeatures: [
        {
          label: `${getDesignLimits(designLimits).pro} Projects`,
          bold: true,
        },
        {
          label: creditLimits?.PRO
            ? `${creditLimits.PRO} Credits ${isWorkspacesEnabled ? '/' : 'per'} month${
                isWorkspacesEnabled ? ' per editor' : ''
              }`
            : '',
          bold: true,
          isGenerationCredits: true,
        },
        { label: '10GB File storage', bold: true },
      ],
      otherFeatures: [
        { label: 'Access to all Premium content' },
        {
          label: '10M+ photos, graphics and fonts',
        },
        { label: 'Kittl AI advanced features' },
        { label: 'High-res vector exports' },
        { label: 'Full commercial license' },
      ],
      comparison: getComparison('PRO', growthBook, { creditLimits }),
    },
    [SubscriptionTierType.EXPERT]: {
      title: 'Expert',
      description:
        'For teams that need even faster workflows and consistency in their designs.',
      buttonLabel: 'Get Expert',
      price: {
        monthly: {
          id: expertMonthlyPrice.id,
          amount: expertMonthlyPrice.amount,
          currency: expertMonthlyPrice.currency,
          addons: getAddons(),
        },
        yearly: {
          id: expertYearlyPrice.id,
          amount: expertYearlyPrice.amount / 12,
          currency: expertYearlyPrice.currency,
          addons: getAddons(),
        },
      },
      featuresLabel: 'Everything in Pro and...',
      mainFeatures: [
        {
          label: 'Unlimited projects',
          bold: true,
        },
        {
          label: getExpertCreditsLabel(
            creditLimits?.EXPERT,
            true,
            selectedExpertAddon,
            true,
            isWorkspacesEnabled
          ),
          bold: true,
          infoIconModalContent:
            getUnlimitedInfoIconModalContentData(selectedExpertAddon),
        },
        { label: '100GB File storage', bold: true },
      ],
      otherFeatures: [
        { label: 'Brand Kits' },
        { label: '30 Image Gen Saved Styles' },
        { label: 'Priority Customer Support' },
        { label: 'Access to Beta features' },
      ],
      comparison: getComparison('EXPERT', growthBook, {
        creditLimits,
        selectedExpertAddon,
      }),
    },
    [SubscriptionTierType.BUSINESS]: {
      title: 'Business',
      description:
        'For businesses who design for multiple brands and require extra security layer.',
      buttonLabel: 'Get Business',
      price: {
        monthly: {
          id: businessMonthlyPrice.id,
          amount: businessMonthlyPrice.amount,
          currency: businessMonthlyPrice.currency,
        },
        yearly: {
          id: businessYearlyPrice.id,
          amount: businessYearlyPrice.amount,
          currency: businessYearlyPrice.currency,
        },
      },
      featuresLabel: 'Everything in Expert and...',
      mainFeatures: [
        {
          label: '3x Teams included',
          bold: true,
        },
        { label: 'File transitioning between teams', bold: true },
        { label: 'Licensing for business', bold: true },
      ],
      otherFeatures: [
        { label: 'Dedicated account support' },
        { label: 'User Analytics' },
        {
          label:
            'Yearly billing with VAT invoice (only for yearly subscription)',
        },
      ],
      comparison: getComparison('BUSINESS', growthBook, { creditLimits }),
    },
  };
};
